import { apiUrl } from './config';
import { productApiPath } from '../utils/slug';
import type { Product, ProductWithVariants, JourneyListResult, WorkflowExecution, WorkflowInput, WorkflowWithDetail, ReferralProfile, ReferralRecord, PointsLedgerEntry, ReferralStats, ReferralDashboardData, DealerApplicationInput, ReferralCodeInfo } from '../../shared/types';

const TOKEN_KEY = 'fyt_admin_token';

export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: { code: string; message: string };
}

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function setToken(token: string): void {
  localStorage.setItem(TOKEN_KEY, token);
}

export function removeToken(): void {
  localStorage.removeItem(TOKEN_KEY);
}

function handleUnauthorized(path: string, status: number) {
  if (status !== 401) return;
  if (!path.startsWith('/api/admin')) return;
  // Don't redirect on the login page or on auth endpoints (LoginPage/AdminLayout handle those)
  if (typeof window !== 'undefined') {
    if (window.location.pathname === '/login') return;
    if (path.startsWith('/api/admin/auth')) return;
  }
  removeToken();
  if (typeof window !== 'undefined') {
    window.location.href = '/login';
  }
}

async function request<T>(method: string, path: string, body?: unknown): Promise<ApiResponse<T>> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  const token = getToken();
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const res = await fetch(apiUrl(path), {
    method,
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  let data: ApiResponse<T>;
  try {
    data = await res.json();
  } catch {
    data = { success: false, error: { code: 'PARSE_ERROR', message: 'Invalid response from server' } };
  }

  handleUnauthorized(path, res.status);

  if (!res.ok) {
    throw new Error(data.error?.message || `Request failed with ${res.status}`);
  }

  return data;
}

export const api = {
  get: <T>(path: string) => request<T>('GET', path),
  post: <T>(path: string, body: unknown) => request<T>('POST', path, body),
  put: <T>(path: string, body: unknown) => request<T>('PUT', path, body),
  delete: <T>(path: string) => request<T>('DELETE', path),
};

// Auth endpoints
export interface LoginResult {
  token: string;
  user: { id: number; username: string; role: string };
}

export async function login(username: string, password: string): Promise<LoginResult> {
  const res = await api.post<LoginResult>('/api/admin/auth/login', { username, password });
  if (!res.success || !res.data) {
    throw new Error(res.error?.message || 'Login failed');
  }
  setToken(res.data.token);
  return res.data;
}

export async function logout(): Promise<void> {
  try {
    await api.post('/api/admin/auth/logout', {});
  } finally {
    removeToken();
  }
}

export async function fetchMe(): Promise<{ id: number; username: string; role: string } | null> {
  const res = await api.get<{ user: { id: number; username: string; role: string } }>('/api/admin/auth/me');
  return res.success && res.data ? res.data.user : null;
}

// Media library endpoints
export interface MediaAsset {
  id: number;
  filename: string;
  originalName: string | null;
  mimeType: string | null;
  size: number | null;
  r2Key: string;
  publicUrl: string;
  altText: string | null;
  category: string;
  tags: string[];
  sizes?: Record<string, string>;
  createdAt: string;
}

export async function listMedia(params?: { q?: string; category?: string; limit?: number; offset?: number }): Promise<ApiResponse<MediaAsset[]> & { total?: number }> {
  const search = new URLSearchParams();
  if (params?.q) search.set('q', params.q);
  if (params?.category) search.set('category', params.category);
  if (params?.limit) search.set('limit', String(params.limit));
  if (params?.offset) search.set('offset', String(params.offset));
  const query = search.toString();
  return api.get<MediaAsset[]>(`/api/admin/media${query ? `?${query}` : ''}`) as Promise<ApiResponse<MediaAsset[]> & { total?: number }>;
}

export async function uploadMedia(
  file: File,
  metadata?: { altText?: string; category?: string; tags?: string; sizes?: { size400?: File; size1200?: File } }
): Promise<ApiResponse<MediaAsset>> {
  const formData = new FormData();
  formData.append('file', file);
  if (metadata?.altText) formData.append('altText', metadata.altText);
  if (metadata?.category) formData.append('category', metadata.category);
  if (metadata?.tags) formData.append('tags', metadata.tags);
  if (metadata?.sizes?.size400) formData.append('size400', metadata.sizes.size400);
  if (metadata?.sizes?.size1200) formData.append('size1200', metadata.sizes.size1200);

  const token = getToken();
  const headers: Record<string, string> = {};
  if (token) headers.Authorization = `Bearer ${token}`;

  const res = await fetch(apiUrl('/api/admin/media/upload'), {
    method: 'POST',
    headers,
    body: formData,
  });

  let data: ApiResponse<MediaAsset>;
  try {
    data = await res.json();
  } catch {
    data = { success: false, error: { code: 'PARSE_ERROR', message: 'Invalid response from server' } };
  }

  handleUnauthorized('/api/admin/media/upload', res.status);

  if (!res.ok) {
    throw new Error(data.error?.message || `Upload failed with ${res.status}`);
  }
  return data;
}

export async function updateMedia(id: number, metadata: { altText?: string; category?: string; tags?: string }): Promise<ApiResponse<MediaAsset>> {
  return api.put<MediaAsset>(`/api/admin/media/${id}`, metadata);
}

export async function deleteMedia(id: number): Promise<ApiResponse<null>> {
  return api.delete<null>(`/api/admin/media/${id}`);
}

// Product endpoints
export interface LocalizedInput {
  'zh-HK'?: string;
  'zh-CN'?: string;
  en?: string;
}

export interface ProductSpecInput {
  key: string;
  labelZh: string;
  labelEn?: string;
  labelCn?: string;
  valueZh: string;
  valueEn?: string;
  valueCn?: string;
}

export interface ProductInput {
  slug?: string;
  sku?: string;
  name: LocalizedInput;
  subtitle?: LocalizedInput;
  description?: LocalizedInput;
  specs?: ProductSpecInput[];
  careInfo?: LocalizedInput;
  usage?: LocalizedInput;
  price: number;
  comparePrice?: number;
  costPrice?: number;
  imageUrl?: string;
  gallery?: string[];
  longImageUrl?: string;
  category?: string;
  tags?: string[];
  stockQuantity?: number;
  isActive?: boolean;
  isFeatured?: boolean;
  sortOrder?: number;
  metaTitle?: LocalizedInput;
  metaDescription?: LocalizedInput;
}

export interface ProductVariantInput {
  sku: string;
  name: LocalizedInput;
  priceAdjustment?: number;
  stock?: number;
  isActive?: boolean;
}

export async function seedProducts() {
  return api.post<{ slug: string; created: boolean }[]>('/api/admin/products/seed', {});
}

export async function listProducts(params?: { q?: string; category?: string; limit?: number; offset?: number }) {
  const search = new URLSearchParams();
  if (params?.q) search.set('q', params.q);
  if (params?.category) search.set('category', params.category);
  if (params?.limit) search.set('limit', String(params.limit));
  if (params?.offset) search.set('offset', String(params.offset));
  const query = search.toString();
  return api.get<Product[]>(`/api/admin/products${query ? `?${query}` : ''}`) as Promise<ApiResponse<Product[]> & { total?: number }>;
}

export async function getProduct(id: number) {
  return api.get<ProductWithVariants>(`/api/admin/products/${id}`);
}

export async function createProduct(body: ProductInput) {
  return api.post<{ id: number; slug: string }>('/api/admin/products', body);
}

export async function updateProduct(id: number, body: Partial<ProductInput>) {
  return api.put<{ id: number }>(`/api/admin/products/${id}`, body);
}

export async function deleteProduct(id: number) {
  return api.delete<null>(`/api/admin/products/${id}`);
}

export async function createVariant(productId: number, body: ProductVariantInput) {
  return api.post<{ id: number }>(`/api/admin/products/${productId}/variants`, body);
}

export async function updateVariant(productId: number, variantId: number, body: Partial<ProductVariantInput>) {
  return api.put<{ id: number }>(`/api/admin/products/${productId}/variants/${variantId}`, body);
}

export async function deleteVariant(productId: number, variantId: number) {
  return api.delete<null>(`/api/admin/products/${productId}/variants/${variantId}`);
}

// Public product endpoints
export async function listPublicProducts(params?: { category?: string; featured?: boolean; limit?: number; offset?: number }) {
  const search = new URLSearchParams();
  if (params?.category) search.set('category', params.category);
  if (params?.featured) search.set('featured', '1');
  if (params?.limit) search.set('limit', String(params.limit));
  if (params?.offset) search.set('offset', String(params.offset));
  const query = search.toString();
  return api.get<Product[]>(`/api/public/products${query ? `?${query}` : ''}`);
}

export async function getPublicProduct(slug: string) {
  const path = productApiPath(slug);
  if (!path) {
    return { success: false, error: { code: 'INVALID_SLUG', message: 'Invalid product slug' } } as ApiResponse<ProductWithVariants>;
  }
  return api.get<ProductWithVariants>(path);
}

export interface CmsSection {
  id: number;
  key: string;
  title: string;
  contentJson: Record<string, unknown>;
  isActive: boolean;
  sortOrder: number;
  updatedAt: string;
}

export async function getPublicCmsSections(): Promise<ApiResponse<CmsSection[]>> {
  // Append a cache-busting timestamp so browsers/CDN never serve a stale cached response.
  return api.get<CmsSection[]>(`/api/public/cms/sections?_t=${Date.now()}`);
}

// Order endpoints
export interface OrderInput {
  customerName: string;
  customerPhone: string;
  customerPhoneCountryCode?: string;
  customerEmail?: string;
  shippingAddress?: string;
  remark?: string;
  items: Array<{
    productId: number;
    variantId?: number;
    quantity: number;
  }>;
  totalAmount: number;
  referralCode?: string;
  dealerCode?: string;
}

export interface Order {
  id: number;
  orderNum: string;
  customerName: string;
  customerPhone: string;
  customerEmail?: string;
  shippingAddress?: string;
  remark?: string;
  adminRemark?: string;
  totalAmount: number;
  status: 'pending' | 'awaiting_payment' | 'paid' | 'preparing' | 'shipped' | 'delivered' | 'cancelled';
  referralCode?: string;
  dealerCode?: string;
  paymentProvider?: string;
  paymentProviderRef?: string;
  paymentUrl?: string;
  paidAt?: string;
  createdAt: string;
}

export interface OrderItem {
  id: number;
  orderId: number;
  productId: number;
  variantId?: number;
  productName: string;
  price: number;
  quantity: number;
}

export interface OrderWithItems extends Order {
  items: OrderItem[];
  paymentProofs?: PaymentProof[];
}

export interface PaymentProof {
  id: number;
  orderId: number;
  proofImageUrl?: string;
  amount?: number;
  status: 'pending' | 'confirmed' | 'rejected';
  confirmedBy?: number;
  confirmedAt?: string;
  createdAt: string;
}

export async function createOrder(body: OrderInput): Promise<ApiResponse<{ orderNum: string; id: number }>> {
  return api.post<{ orderNum: string; id: number }>('/api/public/orders', body);
}

export async function getPublicOrder(orderNum: string): Promise<ApiResponse<OrderWithItems>> {
  return api.get<OrderWithItems>(`/api/public/orders/${orderNum}`);
}

export async function getAdminOrderStats(): Promise<ApiResponse<Record<string, number>>> {
  return api.get<Record<string, number>>('/api/admin/orders/stats');
}

export async function listAdminOrders(params?: { q?: string; status?: string; source?: string; code?: string; limit?: number; offset?: number }): Promise<ApiResponse<Order[]> & { total?: number }> {
  const search = new URLSearchParams();
  if (params?.q) search.set('q', params.q);
  if (params?.status) search.set('status', params.status);
  if (params?.source) search.set('source', params.source);
  if (params?.code) search.set('code', params.code);
  if (params?.limit) search.set('limit', String(params.limit));
  if (params?.offset) search.set('offset', String(params.offset));
  const query = search.toString();
  return api.get<Order[]>(`/api/admin/orders${query ? `?${query}` : ''}`) as Promise<ApiResponse<Order[]> & { total?: number }>;
}

export async function getAdminOrder(id: number): Promise<ApiResponse<OrderWithItems>> {
  return api.get<OrderWithItems>(`/api/admin/orders/${id}`);
}

export async function updateOrderStatus(id: number, status: Order['status'], adminRemark?: string): Promise<ApiResponse<{ id: number; status: Order['status'] }>> {
  return api.put<{ id: number; status: Order['status'] }>(`/api/admin/orders/${id}/status`, { status, adminRemark });
}

export async function resendOrderWhatsApp(id: number): Promise<ApiResponse<{ sent: boolean }>> {
  return api.post<{ sent: boolean }>(`/api/admin/orders/${id}/resend-whatsapp`, {});
}

export async function uploadPaymentProof(orderId: number, file: File): Promise<ApiResponse<{ proofImageUrl: string }>> {
  const formData = new FormData();
  formData.append('file', file);

  const token = getToken();
  const headers: Record<string, string> = {};
  if (token) headers.Authorization = `Bearer ${token}`;

  const res = await fetch(apiUrl(`/api/public/orders/${orderId}/payment-proof`), {
    method: 'POST',
    headers,
    body: formData,
  });

  let data: ApiResponse<{ proofImageUrl: string }>;
  try {
    data = await res.json();
  } catch {
    data = { success: false, error: { code: 'PARSE_ERROR', message: 'Invalid response from server' } };
  }

  handleUnauthorized(`/api/public/orders/${orderId}/payment-proof`, res.status);

  if (!res.ok) {
    throw new Error(data.error?.message || `Upload failed with ${res.status}`);
  }
  return data;
}

export async function listPaymentProofs(orderId: number): Promise<ApiResponse<PaymentProof[]>> {
  return api.get<PaymentProof[]>(`/api/admin/orders/${orderId}/payment-proofs`);
}

export async function confirmPaymentProof(orderId: number, proofId: number, amount?: number): Promise<ApiResponse<{ id: number; status: string; orderStatus: string }>> {
  return api.put<{ id: number; status: string; orderStatus: string }>(`/api/admin/orders/${orderId}/payment-proofs/${proofId}/confirm`, { amount });
}

export async function rejectPaymentProof(orderId: number, proofId: number): Promise<ApiResponse<{ id: number; status: string }>> {
  return api.put<{ id: number; status: string }>(`/api/admin/orders/${orderId}/payment-proofs/${proofId}/reject`, {});
}

// Fulfillment batch endpoints (Phase 4)
export type FulfillmentBatchStatus = 'pending' | 'preparing' | 'packed' | 'shipped' | 'delivered' | 'cancelled';

export interface FulfillmentBatch {
  id: number;
  batchNum: string;
  status: FulfillmentBatchStatus;
  trackingNumber?: string;
  notes?: string;
  dealerCode?: string;
  orderCount: number;
  shippedAt?: string;
  deliveredAt?: string;
  createdAt: string;
}

export interface FulfillmentBatchDetail extends FulfillmentBatch {
  orders: OrderWithItems[];
}

export async function listFulfillmentBatches(params?: { status?: string; limit?: number; offset?: number }): Promise<ApiResponse<FulfillmentBatch[]>> {
  const search = new URLSearchParams();
  if (params?.status) search.set('status', params.status);
  if (params?.limit) search.set('limit', String(params.limit));
  if (params?.offset) search.set('offset', String(params.offset));
  const query = search.toString();
  return api.get<FulfillmentBatch[]>(`/api/admin/fulfillment/batches${query ? `?${query}` : ''}`);
}

export async function getEligibleFulfillmentOrders(): Promise<ApiResponse<Order[]>> {
  return api.get<Order[]>('/api/admin/fulfillment/eligible-orders');
}

export async function createFulfillmentBatch(orderIds: number[], notes?: string): Promise<ApiResponse<FulfillmentBatch>> {
  return api.post<FulfillmentBatch>('/api/admin/fulfillment/batches', { orderIds, notes });
}

export async function getFulfillmentBatch(id: number): Promise<ApiResponse<FulfillmentBatchDetail>> {
  return api.get<FulfillmentBatchDetail>(`/api/admin/fulfillment/batches/${id}`);
}

export async function updateFulfillmentBatch(id: number, body: { trackingNumber?: string; notes?: string }): Promise<ApiResponse<FulfillmentBatch>> {
  return api.put<FulfillmentBatch>(`/api/admin/fulfillment/batches/${id}`, body);
}

export async function updateFulfillmentBatchStatus(id: number, status: FulfillmentBatchStatus, extra?: { trackingNumber?: string }): Promise<ApiResponse<FulfillmentBatch>> {
  return api.put<FulfillmentBatch>(`/api/admin/fulfillment/batches/${id}/status`, { status, ...extra });
}

export async function addOrdersToFulfillmentBatch(id: number, orderIds: number[]): Promise<ApiResponse<{ id: number; added: number; orderCount: number }>> {
  return api.post<{ id: number; added: number; orderCount: number }>(`/api/admin/fulfillment/batches/${id}/orders`, { orderIds });
}

export async function removeOrderFromFulfillmentBatch(id: number, orderId: number): Promise<ApiResponse<{ id: number; orderId: number; orderCount: number }>> {
  return api.delete<{ id: number; orderId: number; orderCount: number }>(`/api/admin/fulfillment/batches/${id}/orders/${orderId}`);
}

// Settings endpoints
export interface PaymentSettings {
  siteUrl?: string;
  currencySymbol?: string;
  priceLocale?: string;
  defaultPhoneCountryCode?: string;
  brandName?: string;
  brandNameEn?: string;
  brandSince?: string;
  brandLogoUrl?: string;
  paymentBankName?: string;
  paymentBankAccount?: string;
  paymentFpsId?: string;
  paymentPayeeName?: string;
  kpayEnabled?: boolean;
}

export interface WhatsAppSettings {
  channel: 'cloudwapi' | 'meta';
  cloudwapiSender?: string;
  adminPhone?: string;
  metaPhoneNumberId?: string;
  hasCloudwapiKey: boolean;
  hasMetaToken: boolean;
}

export interface SystemSettings {
  paymentBankName: string;
  paymentBankAccount: string;
  paymentFpsId: string;
  paymentPayeeName: string;
  kpayEnabled: boolean;
  whatsappChannel: 'cloudwapi' | 'meta';
  whatsappCloudwapiApiKey: string;
  whatsappCloudwapiSender: string;
  whatsappAdminPhone: string;
  whatsappMessageOrderReceived: string;
  whatsappMessagePaymentConfirmed: string;
  whatsappMessageOrderShipped: string;
  metaAccessToken: string;
  metaPhoneNumberId: string;
  siteUrl: string;
  currencySymbol: string;
  priceLocale: string;
  defaultPhoneCountryCode: string;
  brandName: string;
  brandNameEn: string;
  brandSince: string;
  brandLogoUrl: string;
}

export async function getPublicPaymentSettings(): Promise<ApiResponse<PaymentSettings>> {
  return api.get<PaymentSettings>('/api/public/settings/payment');
}

export async function getAdminSettings(): Promise<ApiResponse<SystemSettings>> {
  return api.get<SystemSettings>('/api/admin/settings');
}

export async function updateAdminSettings(settings: Partial<SystemSettings>): Promise<ApiResponse<SystemSettings>> {
  return api.put<SystemSettings>('/api/admin/settings', settings);
}

export async function getWhatsAppSettings(): Promise<ApiResponse<WhatsAppSettings>> {
  return api.get<WhatsAppSettings>('/api/admin/whatsapp/settings');
}

export async function sendWhatsAppTest(payload: { to: string; message: string; channel?: 'cloudwapi' | 'meta' }): Promise<ApiResponse<{ sent: boolean }>> {
  return api.post<{ sent: boolean }>('/api/admin/whatsapp/test', payload);
}

// Journey endpoints (Phase 5)
export async function listJourneys(): Promise<ApiResponse<JourneyListResult>> {
  return api.get<JourneyListResult>('/api/admin/journeys');
}

export async function getJourney(id: number): Promise<ApiResponse<WorkflowWithDetail>> {
  return api.get<WorkflowWithDetail>(`/api/admin/journeys/${id}`);
}

export async function createJourney(body: WorkflowInput): Promise<ApiResponse<WorkflowWithDetail>> {
  return api.post<WorkflowWithDetail>('/api/admin/journeys', body);
}

export async function updateJourney(id: number, body: WorkflowInput): Promise<ApiResponse<WorkflowWithDetail>> {
  return api.put<WorkflowWithDetail>(`/api/admin/journeys/${id}`, body);
}

export async function deleteJourney(id: number): Promise<ApiResponse<{ id: number }>> {
  return api.delete<{ id: number }>(`/api/admin/journeys/${id}`);
}

export async function setJourneysEnabled(enabled: boolean): Promise<ApiResponse<{ enabled: boolean }>> {
  return api.put<{ enabled: boolean }>('/api/admin/journeys/enabled', { enabled });
}

export async function listJourneyExecutions(params?: { status?: string; workflowId?: number; limit?: number; offset?: number }): Promise<ApiResponse<WorkflowExecution[]>> {
  const search = new URLSearchParams();
  if (params?.status) search.set('status', params.status);
  if (params?.workflowId) search.set('workflowId', String(params.workflowId));
  if (params?.limit) search.set('limit', String(params.limit));
  if (params?.offset) search.set('offset', String(params.offset));
  const query = search.toString();
  return api.get<WorkflowExecution[]>(`/api/admin/journeys/executions${query ? `?${query}` : ''}`);
}

export async function cancelJourneyExecution(id: number): Promise<ApiResponse<{ id: number; status: string }>> {
  return api.put<{ id: number; status: string }>(`/api/admin/journeys/executions/${id}/cancel`, {});
}

export interface JourneyRunResult {
  executionId: number;
  execution: WorkflowExecution | null;
  scan: { processed: number; sent: number; skipped: number; failed: number };
}

export async function runJourneyNow(id: number, orderNum: string): Promise<ApiResponse<JourneyRunResult>> {
  return api.post<JourneyRunResult>(`/api/admin/journeys/${id}/run`, { orderNum });
}

export async function previewJourneyMessage(payload: { templateKey: string; body?: string; orderNum?: string }): Promise<ApiResponse<{ rendered: string; sampleOrder: { orderNum: string; customerName: string } }>> {
  return api.post<{ rendered: string; sampleOrder: { orderNum: string; customerName: string } }>('/api/admin/journeys/preview', payload);
}

// Referral & dealer endpoints (Phase 6)

export async function getReferralCodeInfo(code: string): Promise<ApiResponse<ReferralCodeInfo>> {
  return api.get<ReferralCodeInfo>(`/api/public/referral/info/${encodeURIComponent(code)}`);
}

export async function getReferralDashboard(token: string): Promise<ApiResponse<ReferralDashboardData>> {
  return api.get<ReferralDashboardData>(`/api/public/referral/dashboard/${encodeURIComponent(token)}`);
}

export async function getReferralDashboardByCode(code: string): Promise<ApiResponse<ReferralDashboardData>> {
  return api.get<ReferralDashboardData>(`/api/public/referral/dashboard-by-code/${encodeURIComponent(code)}`);
}

export async function submitDealerApplication(input: DealerApplicationInput): Promise<ApiResponse<null>> {
  return api.post<null>('/api/public/dealer/apply', input);
}

export async function getReferralStats(): Promise<ApiResponse<ReferralStats>> {
  return api.get<ReferralStats>('/api/admin/referrals/stats');
}

export async function listReferralProfiles(params?: { role?: string; status?: string; q?: string; limit?: number; offset?: number }): Promise<ApiResponse<{ profiles: ReferralProfile[]; total: number }>> {
  const search = new URLSearchParams();
  if (params?.role) search.set('role', params.role);
  if (params?.status) search.set('status', params.status);
  if (params?.q) search.set('q', params.q);
  if (params?.limit) search.set('limit', String(params.limit));
  if (params?.offset) search.set('offset', String(params.offset));
  const query = search.toString();
  return api.get<{ profiles: ReferralProfile[]; total: number }>(`/api/admin/referrals${query ? `?${query}` : ''}`);
}

export async function getReferralProfileDetail(id: number): Promise<ApiResponse<{ profile: ReferralProfile; records: ReferralRecord[]; ledger: PointsLedgerEntry[] }>> {
  return api.get<{ profile: ReferralProfile; records: ReferralRecord[]; ledger: PointsLedgerEntry[] }>(`/api/admin/referrals/${id}`);
}

export async function setReferralProfileStatus(id: number, status: 'active' | 'pending' | 'suspended'): Promise<ApiResponse<{ id: number; status: string; token: string }>> {
  return api.put<{ id: number; status: string; token: string }>(`/api/admin/referrals/${id}/status`, { status });
}

export async function setDealerCommissionRate(id: number, commissionRate: number): Promise<ApiResponse<{ id: number; commissionRate: number }>> {
  return api.put<{ id: number; commissionRate: number }>(`/api/admin/referrals/${id}/commission`, { commissionRate });
}

export async function adjustReferralPoints(id: number, amount: number, description: string): Promise<ApiResponse<null>> {
  return api.post<null>(`/api/admin/referrals/${id}/points`, { amount, description });
}

export async function regenerateReferralToken(id: number): Promise<ApiResponse<{ token: string }>> {
  return api.post<{ token: string }>(`/api/admin/referrals/${id}/regenerate-token`, {});
}

// KPay hosted cashier endpoints (Phase 9)
export async function createKpayCheckout(orderNum: string): Promise<ApiResponse<{ managedOrderNo: string; payUrl: string; orderExpireMinutes: number }>> {
  return api.post<{ managedOrderNo: string; payUrl: string; orderExpireMinutes: number }>(`/api/public/orders/${encodeURIComponent(orderNum)}/kpay/checkout`, {});
}

export async function getKpayOrderStatus(orderNum: string): Promise<ApiResponse<{ orderNum: string; status: string; paymentProvider: string | null; paidAt: string | null; paid: boolean }>> {
  return api.get<{ orderNum: string; status: string; paymentProvider: string | null; paidAt: string | null; paid: boolean }>(`/api/public/orders/${encodeURIComponent(orderNum)}/kpay/status`);
}

export async function kpayRefundOrder(id: number, refundAmount?: number): Promise<ApiResponse<{ refundOrderNo?: string; result?: number; refundAmountCents: number }>> {
  return api.post<{ refundOrderNo?: string; result?: number; refundAmountCents: number }>(`/api/admin/orders/${id}/kpay/refund`, refundAmount !== undefined ? { refundAmount } : {});
}
